import React, { useState, useEffect } from 'react';
import {
  X,
  Users,
  UserPlus,
  Shield,
  Trash2,
  Check,
  AlertCircle,
} from 'lucide-react';
import api from '../../api/client';

export default function ProjectMembersDrawer({
  projectId,
  isOpen,
  onClose,
  onMembersUpdated,
}) {
  const [members, setMembers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [availableTeams, setAvailableTeams] = useState([]);
  const [targetType, setTargetType] = useState('USER'); // USER or TEAM
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [selectedRole, setSelectedRole] = useState('CONTRIBUTOR');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && projectId) {
      fetchProjectMembers();
      fetchUsersAndTeams();
    }
  }, [isOpen, projectId]);

  const fetchProjectMembers = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/projects/${projectId}`);
      setMembers(res.data?.members || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsersAndTeams = async () => {
    try {
      const [uRes, tRes] = await Promise.all([
        api.get('/users/organization').catch(() => ({ data: [] })),
        api.get('/teams').catch(() => ({ data: [] })),
      ]);
      setAvailableUsers(uRes.data || []);
      setAvailableTeams(tRes.data || []);
    } catch (err) {
      // ignore
    }
  };

  if (!isOpen) return null;

  const handleAddMember = async (e) => {
    e.preventDefault();
    setError('');
    if (targetType === 'USER' && !selectedUserId) {
      setError('Please select a user');
      return;
    }
    if (targetType === 'TEAM' && !selectedTeamId) {
      setError('Please select a team');
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/projects/${projectId}/members`, {
        user_id: targetType === 'USER' ? selectedUserId : undefined,
        team_id: targetType === 'TEAM' ? selectedTeamId : undefined,
        role: selectedRole,
      });

      setSelectedUserId('');
      setSelectedTeamId('');
      fetchProjectMembers();
      if (onMembersUpdated) onMembersUpdated();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add project member');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm('Remove this member from project?')) return;
    try {
      await api.delete(`/projects/${projectId}/members/${memberId}`);
      fetchProjectMembers();
      if (onMembersUpdated) onMembersUpdated();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to remove member');
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: '460px',
        maxWidth: '100vw',
        background: 'var(--bg-secondary)',
        borderLeft: '1px solid var(--border-color)',
        zIndex: 1060,
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '-8px 0 32px rgba(0,0,0,0.4)',
        animation: 'slideInRight 0.25s ease-out',
      }}
    >
      <div
        style={{
          padding: '16px 20px',
          borderBottom: '1px solid var(--border-color)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'var(--bg-surface)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Users size={18} color="var(--accent-primary)" />
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
            Project Members ({members.length})
          </h3>
        </div>
        <button
          onClick={onClose}
          style={{ background: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', padding: '6px' }}
        >
          <X size={20} />
        </button>
      </div>

      <div style={{ padding: '20px', flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {/* Add Member Form */}
        <form
          onSubmit={handleAddMember}
          style={{
            padding: '16px',
            borderRadius: '12px',
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-color)',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <UserPlus size={15} />
            <span>Add Member / Team</span>
          </div>

          {error && (
            <div style={{ padding: '8px 12px', borderRadius: '6px', background: 'rgba(239, 68, 68, 0.15)', color: 'var(--danger)', fontSize: '12px' }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={() => setTargetType('USER')}
              className={targetType === 'USER' ? 'btn-primary' : 'btn-secondary'}
              style={{ flex: 1, padding: '6px 12px', fontSize: '12px' }}
            >
              User
            </button>
            <button
              type="button"
              onClick={() => setTargetType('TEAM')}
              className={targetType === 'TEAM' ? 'btn-primary' : 'btn-secondary'}
              style={{ flex: 1, padding: '6px 12px', fontSize: '12px' }}
            >
              Team
            </button>
          </div>

          {targetType === 'USER' ? (
            <select
              className="input-field"
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              style={{ width: '100%' }}
            >
              <option value="">Select a user...</option>
              {availableUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.email} ({u.role})
                </option>
              ))}
            </select>
          ) : (
            <select
              className="input-field"
              value={selectedTeamId}
              onChange={(e) => setSelectedTeamId(e.target.value)}
              style={{ width: '100%' }}
            >
              <option value="">Select a team...</option>
              {availableTeams.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          )}

          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>
              Project Role
            </label>
            <select
              className="input-field"
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              style={{ width: '100%' }}
            >
              <option value="VIEWER">Viewer (Read-only)</option>
              <option value="CONTRIBUTOR">Contributor (Add/edit tasks)</option>
              <option value="EDITOR">Editor (Manage tasks & settings)</option>
              <option value="MANAGER">Manager (Full project control)</option>
            </select>
          </div>

          <button
            type="submit"
            className="btn-primary"
            disabled={submitting}
            style={{ width: '100%', marginTop: '4px' }}
          >
            {submitting ? 'Adding...' : 'Grant Access'}
          </button>
        </form>

        {/* Existing Members List */}
        <div>
          <h4 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '10px' }}>
            ACTIVE COLLABORATORS
          </h4>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '16px', color: 'var(--text-secondary)' }}>Loading members...</div>
          ) : members.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '16px', color: 'var(--text-secondary)', fontSize: '13px' }}>
              No members assigned yet.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {members.map((m) => (
                <div
                  key={m.id}
                  style={{
                    padding: '12px 14px',
                    borderRadius: '8px',
                    background: 'var(--bg-surface)',
                    border: '1px solid var(--border-color)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>
                      {m.user_email || m.team_name || 'Member'}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                      {m.team_name ? '👥 Team Access' : '👤 Direct User'}
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: 600,
                        background: 'rgba(59, 130, 246, 0.15)',
                        color: 'var(--accent-primary)',
                      }}
                    >
                      {m.role}
                    </span>

                    {m.role !== 'OWNER' && (
                      <button
                        onClick={() => handleRemoveMember(m.id)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: 'var(--danger)',
                          cursor: 'pointer',
                          padding: '4px',
                        }}
                        title="Remove member"
                      >
                        <Trash2 size={15} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
